import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { FormControl, UntypedFormGroup } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AutoUnsubscribe } from '../../../../common/decorators/auto-unsubscribe.decorator';
import { FilterName } from '../../../../store/models/filter.model';
import { MonitoringFiltersFieldComponent } from '../filters-field/monitoring-filters-field.component';
import { MonitoringSelectProfileComponent } from '../select-profile/monitoring-select-profile.component';
import { MonitoringPageService } from '../page/monitoring-page.service';
import { IntrackFeatureModule } from '../../../../intrack-feature.module';
import { SvgIconComponent } from '../../../svg-icon/svg-icon.component';
import { ActionButtonComponent } from '../../../action-button';
import {
  chipsInputOptionValueMap,
  chipsInputSplitPatternMap,
  DraggableFilterType,
  FilterState,
  RecordTypeFilterKey,
  recordTypeFilterMap,
  RecordTypeFilterOptionValueKey,
  searchCheckboxAutocompleteWidth,
} from '../../../../store/constants';
import { ButtonAction, ButtonSize } from '../../../action-button/constants';
import { ShipmentFilterName } from '../../../../store';
import { IconNames } from '../../../../common/constants/icon-names';

@Component({
  selector: 'ui-shipment-monitoring-filters',
  templateUrl: './shipment-monitoring-filters.component.html',
  styleUrls: ['./shipment-monitoring-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IntrackFeatureModule,
    TranslateModule,
    DragDropModule,
    SvgIconComponent,
    ActionButtonComponent,
    MonitoringFiltersFieldComponent,
    MonitoringSelectProfileComponent,
  ],
})
@AutoUnsubscribe
export class ShipmentMonitoringFiltersComponent {
  @Input() filtersForm: UntypedFormGroup;
  @Input() selectedProfileForm: UntypedFormGroup;
  @Input() isCustomizedMode: boolean;
  @Input() isFiltersCustomizable = true;
  @Input() enabledFilters: FilterName[];
  @Input() disabledFilters: FilterName[];
  @Input() filterTypeByName: Map<FilterName, DraggableFilterType>;
  @Input() filterSelectOptionsByName: Map<FilterName, string[]>;
  @Input() canCreateOrUpdateProfile: boolean;
  @Input() isUpdateProfileAvailable: boolean;
  @Input() isProfileVisible = false;
  @Input() defaultProfileControl?: FormControl<boolean>;
  @Output() removeProfile = new EventEmitter<void>();
  @Output() selectProfile = new EventEmitter<void>();
  @Output() updateProfile = new EventEmitter<void>();
  @Output() enableCustomizeMode = new EventEmitter<void>();
  @Output() applyFilters = new EventEmitter<void>();
  @Output() openCreateProfileDialog = new EventEmitter<void>();
  @Output() updateFilterSettings = new EventEmitter<void>();
  @Output() cancelUpdatingFilterSettings = new EventEmitter<void>();
  buttonAction = ButtonAction;
  buttonSize = ButtonSize;
  transportFiltersState = FilterState;
  subscriptions: Subscription[] = [];
  chipsInputOptionValueMap: Map<string, RecordTypeFilterOptionValueKey[]> = chipsInputOptionValueMap;
  recordTypeFilterMap: Map<string, RecordTypeFilterKey[]> = recordTypeFilterMap;
  searchCheckboxAutocompleteWidth: Map<ShipmentFilterName, string | number> = searchCheckboxAutocompleteWidth;
  chipsInputSplitPatternMap: Map<string, RegExp> = chipsInputSplitPatternMap;
  iconNameList = IconNames;

  @ViewChildren(MonitoringFiltersFieldComponent)
  monitoringFiltersFieldComponent: QueryList<MonitoringFiltersFieldComponent>;
  @ViewChild(MonitoringSelectProfileComponent)
  monitoringSelectProfileComponent: MonitoringSelectProfileComponent;

  constructor(private monitoringPageService: MonitoringPageService) {}

  markForCheck(): void {
    this.monitoringFiltersFieldComponent.forEach((component: MonitoringFiltersFieldComponent) =>
      component.cd.markForCheck(),
    );
  }

  apply(): void {
    if (this.monitoringPageService.matDrawer$.getValue()) {
      this.monitoringPageService.close();
    }

    this.applyFilters.emit();
  }

  drop(event: CdkDragDrop<string[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
  }

  changeFieldState(filterName: FilterName, targetState: FilterState): void {
    let currentContainer: FilterName[];
    let targetContainer: FilterName[];

    if (targetState === FilterState.Enabled) {
      currentContainer = this.disabledFilters;
      targetContainer = this.enabledFilters;
    } else {
      currentContainer = this.enabledFilters;
      targetContainer = this.disabledFilters;
    }

    const currentIndex = currentContainer.indexOf(filterName);
    const targetIndex = filterName === ShipmentFilterName.References ? 0 : targetContainer.length;

    transferArrayItem(currentContainer, targetContainer, currentIndex, targetIndex);
  }
}
